import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@talentmesh/ui/components/dialog';
import { Spinner } from '@talentmesh/ui/components/spinner';
import { TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FieldError, FormProvider } from 'react-hook-form';
import useAISearchProgress from '../../../api/useAISearchProgress';
import {
    DefaultRefineSearchFilters,
    RefineSearchFilterFormValues,
    useSearchFilters,
} from '../../../api/useSearchFilters';
import { useUpdateSearchParametersFilter } from '../../../api/useUpdateSearchParametersFilter';
import { useIdentifiedListContext } from '../utils/IdentifiedListContext';
import FiltersForm from './FiltersTab/FiltersForm';
import RefineSearchHeader from './RefineSearchHeader';
import {
    useRefineSearchFiltersForm,
    useRefineSearchFiltersFormContext,
    oneFilterMinimumRule,
} from './useRefineSearchFiltersForm';
import { useRecruitmentId } from '@/Hooks';
import UIStrings from '@/Utils/UIStrings';

interface RefineFilterDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

interface RefineFilterDialogFormProps {
    onOpenChange: (open: boolean) => void;
}

const RefineFilterDialogForm = ({ onOpenChange }: RefineFilterDialogFormProps) => {
    const { setRefineSearchFilters } = useIdentifiedListContext();
    const form = useRefineSearchFiltersForm();
    const [globalValidationError, setGlobalValidationError] = useState<string | undefined>(undefined);
    const { trigger } = useUpdateSearchParametersFilter();

    useEffect(() => {
        const validationErrors = form.formState.errors;

        if (oneFilterMinimumRule in validationErrors) {
            const { message } = validationErrors[oneFilterMinimumRule] as FieldError;
            setGlobalValidationError(message);
        } else {
            setGlobalValidationError(undefined);
        }
    }, [form.formState.errors]);

    const handleApplyChange = async (filterValues: RefineSearchFilterFormValues) => {
        await trigger({
            jobTitleSearchPeriod: filterValues.jobTitleSearchPeriod,
            jobTitles: filterValues.jobTitles.map((jt) => jt.label) || [],
            companiesSearchPeriod: filterValues.companiesSearchPeriod,
            companies: filterValues.companies.map((c) => c.label) || [],
            locationRadius: filterValues.locationRadius,
            locations:
                filterValues.locations.map((l) => ({
                    placeId: l.placeId,
                    placeType: l.placeType,
                    title: l.title,
                    position: l.position,
                    locality: l.locality,
                    subRegion: l.subRegion,
                    region: l.region,
                    country: l.country,
                    localityName: l.localityName,
                    regionName: l.regionName,
                })) || [],
            keywords: filterValues.keywords.map((k) => k.label) || [],
            minYearsOfExperience:
                filterValues.yearsOfExperience?.[0] ?? DefaultRefineSearchFilters.yearsOfExperience[0],
            maxYearsOfExperience:
                filterValues.yearsOfExperience?.[1] ?? DefaultRefineSearchFilters.yearsOfExperience[1],
            includeEmptyExperience:
                filterValues.includeEmptyExperience ?? DefaultRefineSearchFilters.includeEmptyExperience,
            languages: filterValues.languages.map((l) => l.label) || [],
            languageProficiency: filterValues.languageProficiency,
        });

        setRefineSearchFilters(filterValues);
        onOpenChange(false);
    };

    return (
        <FormProvider {...form}>
            <form
                className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-hidden"
                onSubmit={form.handleSubmit(handleApplyChange)}
            >
                <DialogHeader className="shrink-0">
                    <RefineSearchHeader
                        title={UIStrings.RefineFilter}
                        onCancel={() => onOpenChange(false)}
                        useFormContext={useRefineSearchFiltersFormContext}
                    />
                </DialogHeader>
                <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-hidden">
                    {globalValidationError && (
                        <div className="border-destructive bg-destructive/10 text-destructive flex shrink-0 items-center gap-3 rounded-lg border px-4 py-3 text-sm">
                            <TriangleAlert className="h-5 w-5 shrink-0" />
                            {globalValidationError}
                        </div>
                    )}
                    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                        <FiltersForm />
                    </div>
                </div>
            </form>
        </FormProvider>
    );
};

const RefineFilterDialog = ({ open, onOpenChange }: RefineFilterDialogProps) => {
    const recruitmentId = useRecruitmentId();
    const { data: progressData, isLoading: isProgressLoading } = useAISearchProgress();
    const { data: searchFiltersData, isLoading: isSearchFiltersLoading } = useSearchFilters(recruitmentId!);
    const isFormDataLoading =
        isProgressLoading || progressData?.isInProgress || isSearchFiltersLoading || !searchFiltersData;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                showCloseButton={false}
                className="flex max-h-[90vh] min-h-0 max-w-5xl! flex-col gap-0 overflow-hidden p-5"
                data-cy="refineFilterDialog"
            >
                <DialogTitle className="sr-only">{UIStrings.RefineFilter}</DialogTitle>
                <DialogDescription className="sr-only">
                    {UIStrings.AISearch} {UIStrings.RefineFilter}
                </DialogDescription>
                {open &&
                    (isFormDataLoading ? (
                        <div
                            className="flex min-h-40 flex-1 items-center justify-center"
                            data-cy="refineFilterDialogLoading"
                        >
                            <Spinner className="text-primary size-8" />
                        </div>
                    ) : (
                        <RefineFilterDialogForm onOpenChange={onOpenChange} />
                    ))}
            </DialogContent>
        </Dialog>
    );
};

export default RefineFilterDialog;
